/**
 * Le document de composition côté client — le pendant JS de `CompositionDocument` (Ruby) :
 * construction de layers et lectures dérivées, sans aucun hash ni normalisation (plan §5,
 * point de vigilance — la vérité d'empreinte reste serveur).
 *
 * - `fullFrameBgLayer` / `draftSlide` : miroir assumé de `Composition.draft_bg_layer` /
 *   `CompositionDocument::Seed.images`. Nécessaires côté JS parce que l'éditeur travaille sur un état local
 *   non sauvé — un endpoint serveur d'ajout de slide forkerait cet état.
 * - `posterLayers` : la page fabric readonly qui représente une composition vidéo sans la
 *   rendre (plan §6.8 — toute preview est un rendu virtuel, jamais un OutputFile).
 */
import {
  segmentOutputDuration,
  type Composition,
  type Overlay,
  type Segment,
} from "./composition_v3";
import { BG_LAYER_ID, colorBackgroundLayer } from "./fabric_background";

/** Les clés d'un nœud `image` qui ne sont pas à la lib fabric : l'identité du média (le
 *  `content_medium_id` posé à côté de la source — règle du dialecte, plan Composition §5). */
export interface ImageMeta {
  content_medium_id?: number;
  [key: string]: unknown;
}

export interface BgLayer {
  type: "ImageFrame";
  layerId: "bg";
  /** `src` absent = fond en attente : le fichier du média n'existe pas encore. */
  image: ImageMeta & { src?: string; cropX: number; cropY: number; scale: number };
  left: number;
  top: number;
  angle: number;
  frameWidth: number;
  frameHeight: number;
  scaleX: number;
  scaleY: number;
  clipShape: "rect";
  lockMode: "full";
  selectable: false;
  evented: false;
}

export interface Slide {
  id: string;
  layers: object[];
  repeat?: object;
  /** LEGACY_ERB — la directive de fond HTML d'une page de template (famille ERB). */
  background?: { html?: { template_key?: string } };
}

/** LEGACY_ERB — le gabarit HTML d'une page (famille ERB) : la directive du template, ou la
 *  provenance du fond nu d'une instance (CompositionDocument::LegacyHtml). */
export function legacyHtmlOf(slide: Slide | undefined): string | null {
  if (!slide) return null;
  const bg = (slide.layers || []).find((layer) => (layer as { layerId?: string }).layerId === BG_LAYER_ID) as
    { image?: { html?: { template_key?: string } } } | undefined;
  return slide.background?.html?.template_key || bg?.image?.html?.template_key || null;
}

/** L'identité d'un média de la bibliothèque, telle qu'elle se pose dans un nœud `image`. */
export function mediumMeta(contentMedium: { id?: number | string | null }): ImageMeta {
  return contentMedium.id ? { content_medium_id: Number(contentMedium.id) } : {};
}

// `offsetX`/`offsetY` : les documents d'avant (la lib les convertit à la lecture)
const LIB_IMAGE_KEYS = ["src", "cropX", "cropY", "scale", "offsetX", "offsetY"];

/** Les clés d'identité d'un nœud `image` exporté — ce qui doit suivre la source quand le
 *  calque change de forme (promotion en fond, recalage). */
export function imageMetaOf(image: Record<string, unknown> | undefined): ImageMeta {
  return Object.fromEntries(Object.entries(image || {}).filter(([key]) => !LIB_IMAGE_KEYS.includes(key)));
}

/** Le miroir de CompositionDocument.blank_page? : rien que son fond blanc d'origine (ou
 *  rien : transparente), ni intension. */
export function isBlankSlide(slide: Slide): boolean {
  return isBlankLayers(slide.layers || []) && !slide.repeat && !slide.background?.html; // LEGACY_ERB
}

const WHITE = ["#ffffff", "#fff", "white"];
const BLACK = ["#000000", "#000", "black"];

/** Le miroir de CompositionDocument.blank_segment? : une séquence vierge — un plan à
 *  fournir, le bouche-trou dérivé, ou un aplat noir sans média (le fond par défaut d'une
 *  vidéo, le pendant du fond blanc d'une page). */
export function isBlankSegment(seg: Segment): boolean {
  if (seg.placeholder || seg._trailing) return true;
  const src = seg.src as Record<string, unknown> | null | undefined;
  return seg.type === "color" && (!src || !Object.keys(src).length) &&
    BLACK.includes(String(seg.backgroundColor ?? "").toLowerCase());
}

/** Le miroir de CompositionDocument.blank_layers? : aucun calque, ou le seul fond blanc
 *  d'une page neuve. */
export function isBlankLayers(layers: object[]): boolean {
  if (layers.length === 0) return true;
  if (layers.length > 1) return false;
  const bg = layers[0] as { layerId?: string; image?: unknown; bindings?: object; fill?: unknown };
  return bg.layerId === BG_LAYER_ID && !bg.image && !Object.keys(bg.bindings || {}).length &&
    WHITE.includes(String(bg.fill).toLowerCase());
}

export interface PreviewPage {
  width: number;
  height: number;
  layers: object[];
  /** Le fond du segment poster quand il n'est pas un média : une valeur CSS (hex ou
   *  linear-gradient), à peindre SOUS le canvas — fabric ne connaît pas les dégradés CSS. */
  background?: string;
}

/** Un ImageFrame plein cadre centré dans l'espace canvas — l'image couvre (mode cover natif).
 *  Sans source mais avec un média (`meta.content_medium_id`) : un fond en attente. */
export function fullFrameBgLayer(
  src: string | null | undefined,
  width: number,
  height: number,
  meta: ImageMeta = {},
): BgLayer | null {
  if (!src && !meta.content_medium_id) return null;

  return {
    type: "ImageFrame",
    layerId: "bg",
    image: { ...meta, ...(src ? { src } : {}), cropX: 0.5, cropY: 0.5, scale: 1 },
    left: width / 2,
    top: height / 2,
    angle: 0,
    frameWidth: width,
    frameHeight: height,
    scaleX: 1,
    scaleY: 1,
    clipShape: "rect",
    lockMode: "full",
    selectable: false,
    evented: false,
  };
}

/** Une page vierge : un design à écrire, sur son fond blanc — le « + » ouvre l'éditeur
 *  dessus, les médias arrivent ensuite (toolbox, drop, médias dynamiques). Le pendant de
 *  CompositionDocument.blank_page. */
export function blankSlide(width: number, height: number): Slide {
  return {
    id: `slide-${crypto.randomUUID()}`,
    layers: [colorBackgroundLayer(width, height, "#ffffff")],
  };
}

/** Une slide neuve amorcée avec son média (le pendant client de CompositionDocument::Seed.images) : son fond
 *  porte le média, avec sa source dès que le fichier existe. */
export function draftSlide(
  contentMedium: { id: number | string; url?: string | null },
  width: number,
  height: number,
): Slide {
  const bg = fullFrameBgLayer(contentMedium.url, width, height, mediumMeta(contentMedium));

  return { id: `slide-${crypto.randomUUID()}`, layers: bg ? [bg] : [] };
}

/**
 * Le poster d'une composition vidéo : sa PREMIÈRE séquence, quel que soit son type, avec ses
 * calques au repos — ceux dont l'intervalle chevauche la séquence, tels que posés (les
 * `_fabricLayers` sont l'état posé ; les animations vivent à côté, elles ne comptent pas).
 * Une vidéo se montre sur sa frame d'entrée, demandée à Cloudinary (`so_<t>`), repli sur
 * `fallback_url` (la photo d'origine, posée par resolve_nodes!). Miroir serveur :
 * Composition::Poster (même séquence, même frame).
 */
export function posterLayers(doc: Composition): PreviewPage {
  const width = doc.width || 1080;
  const height = doc.height || 1080;
  const segment = posterSegment(doc);
  if (!segment) return { width, height, layers: [] };

  const start = timelineStart(doc, segment);
  const end = start + segmentOutputDuration(segment);
  const bg = fullFrameBgLayer(segmentFrameUrl(segment), width, height);
  // Un segment sans média (aplat, dégradé) : son backgroundColor est une valeur CSS — le
  // fond passe en `background`, peint sous le canvas par la surface.
  const background = !bg && segment.backgroundColor ? segment.backgroundColor : undefined;
  const overlayLayers = (doc.overlays || [])
    .filter((o) => !o._hidden && (o.start ?? 0) < end && (o.end ?? Infinity) > start)
    .flatMap((o) => (o as Overlay & { _fabricLayers?: object[] })._fabricLayers || []);

  return { width, height, layers: [...(bg ? [bg] : []), ...overlayLayers], background };
}

/** La séquence du poster : la première, hors bouche-trou `_trailing` (l'aplat dérivé qui
 *  couvre les overlays — le choisir peindrait du noir) et hors durée nulle. */
export function posterSegment(doc: Composition): Segment | undefined {
  return (doc.segments || []).find((seg) => !seg._trailing && segmentOutputDuration(seg) > 0);
}

function timelineStart(doc: Composition, segment: Segment): number {
  let cursor = 0;
  for (const seg of doc.segments || []) {
    if (seg === segment) return cursor;
    cursor += segmentOutputDuration(seg);
  }
  return 0;
}

// L'image d'une séquence : son média, ou pour une vidéo sa frame d'ENTRÉE (le point de départ
// dans la source).
function segmentFrameUrl(segment: Segment): string | null {
  const url = segment.src?.url;
  if (segment.type !== "video") return url || segment.src?.fallback_url || null;
  if (!url) return segment.src?.fallback_url || null;

  return cloudinaryFrameUrl(url, segment.sourceStart ?? 0) || segment.src?.fallback_url || null;
}

// Une frame jpg extraite par Cloudinary (`so_<t>`) — seule voie sans seek vidéo client.
// Url non-Cloudinary (S3 à terme) : null, le repli fallback_url prend la main.
export function cloudinaryFrameUrl(url: string, time: number): string | null {
  if (!/res\.cloudinary\.com\/.+\/video\/upload\//.test(url)) return null;

  return url
    .replace("/video/upload/", `/video/upload/so_${time.toFixed(2)}/`)
    .replace(/\.[a-z0-9]+(\?.*)?$/i, ".jpg$1");
}

/**
 * Les layers d'une vignette : les sources ImageFrame bornées à `maxSide` par Cloudinary
 * (`c_limit`, jamais d'upscale). Un canvas de 48px qui downscale la source pleine résolution
 * en un seul drawImage crénèle (bilinéaire navigateur) ET télécharge l'image entière — le
 * redimensionnement appartient au CDN, comme pour l'ex cl_image_tag. Sans risque pour les
 * cadrages : le coverScale d'ImageFrame se recalcule des dimensions naturelles, `image.scale`
 * et les offsets sont relatifs au frame. Les autres layers image (logos posés en scaleX
 * absolu) passent tels quels.
 */
export function thumbnailLayers(layers: object[], maxSide: number): object[] {
  return layers.map((layer) => {
    const frame = layer as { type?: string; image?: { src?: string } };
    if (frame.type !== "ImageFrame" || !frame.image?.src) return layer;

    return { ...frame, image: { ...frame.image, src: boundedSrc(frame.image.src, maxSide) } };
  });
}

// La borne s'AJOUTE à la fin de la chaîne de transformations, elle ne s'insère jamais devant :
// une url peut déjà porter un cadrage qui la définit (l'avatar d'une company est un
// `c_crop,x_,y_` sur son image d'origine), et préfixer réordonnerait la chaîne — Cloudinary
// appliquerait la borne à l'image entière, puis le crop aux coordonnées d'origine sur une
// image devenue plus petite : le cadrage part ailleurs. En queue, le crop garde sa matière et
// la borne ne fait que limiter le résultat.
function boundedSrc(url: string, maxSide: number): string {
  const match = url.match(/(res\.cloudinary\.com\/[^/]+\/(?:image|video)\/upload\/)(.*)$/);
  if (!match) return url;

  const [, prefix, rest] = match;
  // Ce qui suit /upload/ est soit la version + le public_id (v123/abc.jpg), soit une chaîne
  // de transformations qui les précède — la borne se glisse juste avant la version.
  const version = rest.search(/(^|\/)v\d+\//);
  const transforms = version === -1 ? "" : rest.slice(0, version === 0 ? 0 : version + 1);
  const asset = version === -1 ? rest : rest.slice(version === 0 ? 0 : version + 1);

  // q_auto : sans elle Cloudinary encode au défaut, et un jpg servi quasi 1:1 au canvas
  // montre ses artefacts de compression tels quels.
  return `${url.slice(0, url.indexOf(prefix))}${prefix}${transforms}w_${maxSide},h_${maxSide},c_limit,q_auto:good/${asset}`;
}
